import React from 'react';
import intl from 'react-intl-universal';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormItem, UsersPicker, Datepicker } from '@getsynapse/design-system';
import { REQUEST_TABLE_FILTERS } from 'utils/constants';
import { Option, rangeDateType, UserAvatars } from 'utils/customTypes';
import {
  getInitialUsersPickerValue,
  getInitialValueForDropDown,
  getInitialDatePickerValue,
} from 'utils/functions';
import {
  RangeFilter,
  RequestFilters,
  RequestFiltersKey,
} from 'utils/types/filters';
import debounce from 'lodash/debounce';
import { isArrayOfOptions, isDateRangeObject } from 'utils/typeGuards';
import MultiSelectDropdown from 'Organisms/MultiSelectDropdow/MultiSelectDropdown';
import useActiveRequestFilters from 'Pages/RequestsListPage/hooks/useActiveRequestFilters';
import { getUserSummaries } from 'state/normalized/userSummaries/userSummariesSlice';
import { userSummarySelectors } from 'state/normalized/userSummaries/userSummariesSlice';
import { getLdUserSummaries } from 'state/normalized/userSummaries/LDUserSummariesSlice';
import { ldUserSummarySelectors } from 'state/normalized/userSummaries/LDUserSummariesSlice';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';
import ProjectsDropdown from './ProjectsDropdown/ProjectsDropdown';
import {
  businessUnitSummarySelectors,
  getBusinessUnitSummaries,
} from 'state/normalized/businessUnitSummaries/businessUnitSummariesSlice';
import {
  formSummarySelectors,
  getFormSummaries,
} from 'state/normalized/formSummaries/formSummariesSlice';

const ActiveRequestsFiltersForm: React.FC<{
  filters: RequestFilters;
  onUpdateFilters: (newFilters: RequestFilters) => void;
}> = ({ filters, onUpdateFilters }) => {
  const dispatch = useDispatch();
  const hasMore = useSelector(userSummarySelectors.selectHasMore);
  const ldHasMore = useSelector(ldUserSummarySelectors.selectLDHasMore);
  const businessUnitsLinksNext = useSelector(
    businessUnitSummarySelectors.selectLinksNext
  );
  const canFetchMoreForms = useSelector(
    formSummarySelectors.selectCanFetchMore
  );
  const [requesterName, handleFilterChange] = useState<string | undefined>();
  const [ownerName, handleOwnerFilterChange] = useState<string | undefined>();
  const [businessUnitName, handleBusinessUnitFilterChange] = useState<
    string | undefined
  >();
  const [formSearchTitle, setFormSearchTitle] = useState<string | undefined>();

  const { getFilterOptionsByKey } = useActiveRequestFilters();
  const statusOptions = getFilterOptionsByKey(REQUEST_TABLE_FILTERS.STATUS);
  const priorityOptions = getFilterOptionsByKey(REQUEST_TABLE_FILTERS.PRIORITY);
  const requesterOptions = getFilterOptionsByKey(
    REQUEST_TABLE_FILTERS.REQUESTER
  ) as UserAvatars[];
  const ownerOptions = getFilterOptionsByKey(
    REQUEST_TABLE_FILTERS.OWNERS_FULL_LIST
  ) as UserAvatars[];
  const businessUnitOptions = getFilterOptionsByKey(
    REQUEST_TABLE_FILTERS.BUSINESS_TEAMS
  );
  const linkedProjectOptions = getFilterOptionsByKey(
    REQUEST_TABLE_FILTERS.LINKED_PROJECTS
  );
  const formOptions = getFilterOptionsByKey(REQUEST_TABLE_FILTERS.FORM);

  const isDateRangeFilterEmpty = (filter: RangeFilter) =>
    filter.from === null && filter.to === null;

  useEffect(() => {
    dispatch(getUserSummaries({}));
    dispatch(getBusinessUnitSummaries({}));
    dispatch(getFormSummaries({}));
    dispatch(getLdUserSummaries({}));
  }, [dispatch]);

  const updateFilters = (
    filterKey: RequestFiltersKey,
    value: Option[] | rangeDateType | string,
    rangeFilterKey: 'from' | 'to' | null = null
  ) => {
    let filtersCopy = { ...filters } as RequestFilters;
    if (isArrayOfOptions(value)) {
      if (value.length === 0) {
        delete filtersCopy[filterKey];
      } else {
        filtersCopy[filterKey] = value.map((option) => option.value);
      }
    } else if (isDateRangeObject(value)) {
      const filterValue = value as rangeDateType;
      filtersCopy[filterKey] = {
        ...(filtersCopy[filterKey] as RangeFilter),
        [rangeFilterKey as string]: filterValue.startDate
          ? new Date(filterValue.startDate).toISOString()
          : null,
      };
      if (isDateRangeFilterEmpty(filtersCopy[filterKey] as RangeFilter)) {
        delete filtersCopy[filterKey];
      }
    }
    onUpdateFilters(filtersCopy);
  };

  const loadMoreOptions = useCallback(async () => {
    if (hasMore) {
      await dispatch(
        getUserSummaries({
          pageSize: DEFAULT_PAGE_SIZE,
          userName: requesterName,
        })
      );
    }
  }, [dispatch, hasMore, requesterName]);

  const loadMoreOwnerOptions = useCallback(async () => {
    // this is to enable lazy loading for owners
    if (ldHasMore) {
      await dispatch(
        getLdUserSummaries({
          pageSize: DEFAULT_PAGE_SIZE,
          userName: ownerName,
        })
      );
    }
  }, [ownerName, dispatch, ldHasMore]);

  const loadMoreBusinessUnitOptions = useCallback(async () => {
    // this is to enable lazy loading for businessUnits
    // for local testing, have approx 20 businesUnits in db,
    // set this pagesize to 7 and this LIST_BUSINESS_USERS_DEFAULT_LIMIT in BE to 7
    if (!!businessUnitsLinksNext) {
      await dispatch(
        getBusinessUnitSummaries({
          pageSize: DEFAULT_PAGE_SIZE,
          businessUnitName,
        })
      );
    }
  }, [dispatch, businessUnitsLinksNext, businessUnitName]);

  const loadMoreForms = useCallback(async () => {
    if (canFetchMoreForms) {
      dispatch(
        getFormSummaries({
          search: formSearchTitle,
          fetchNext: true,
        })
      );
    }
  }, [dispatch, canFetchMoreForms, formSearchTitle]);

  const onFormsFilterChange = debounce((value) => {
    setFormSearchTitle(value);
  }, 500);

  const onFilterChange = debounce((value) => {
    handleFilterChange(value);
  }, 500);

  const onOwnerFilterChange = debounce((value) => {
    handleOwnerFilterChange(value);
  }, 500);

  const onBusinessUnitFilterChange = debounce((value) => {
    handleBusinessUnitFilterChange(value);
  }, 500);

  useEffect(() => {
    if (requesterName) {
      dispatch(
        getUserSummaries({
          pageSize: DEFAULT_PAGE_SIZE,
          userName: requesterName,
        })
      );
    }
  }, [requesterName, dispatch]);

  useEffect(() => {
    if (ownerName) {
      dispatch(
        getLdUserSummaries({
          userName: ownerName,
          pageSize: DEFAULT_PAGE_SIZE,
        })
      );
    }
  }, [ownerName, dispatch]);

  useEffect(() => {
    if (businessUnitName) {
      dispatch(
        getBusinessUnitSummaries({
          pageSize: DEFAULT_PAGE_SIZE,
          businessUnitName: businessUnitName,
        })
      );
    }
  }, [businessUnitName, dispatch]);

  useEffect(() => {
    if (formSearchTitle) {
      dispatch(
        getFormSummaries({
          search: formSearchTitle,
        })
      );
    }
  }, [formSearchTitle, dispatch]);

  return (
    <div className='mt-8 mb-12 flex flex-col space-y-5'>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.STATUS')}>
        <MultiSelectDropdown
          options={statusOptions}
          values={getInitialValueForDropDown(
            statusOptions,
            filters[REQUEST_TABLE_FILTERS.STATUS] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(REQUEST_TABLE_FILTERS.STATUS, options)
          }
          triggerProps={{
            'aria-label': intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.STATUS'),
            'data-testid': 'activeRequests-filters__status-picker',
          }}
          listProps={{
            'data-testid': 'requests-filters__status__options-list',
          }}
          placeholder={intl.get('SELECT')}
        />
      </FormItem>
      <FormItem
        label={intl.get('REQUEST_PAGE.BASIC_DETAILS.FIELDS.REQUEST_CREATOR')}
      >
        <UsersPicker
          deactivatedUserText={intl.get('DEACTIVATED')}
          usersList={requesterOptions}
          onChange={(users) => {
            updateFilters(REQUEST_TABLE_FILTERS.REQUESTER, users);
          }}
          selectedUsersList={getInitialUsersPickerValue(
            requesterOptions,
            filters[REQUEST_TABLE_FILTERS.REQUESTER] as string[]
          )}
          triggerText={intl.get(
            'REQUEST_PAGE.BASIC_DETAILS.FIELDS.REQUEST_CREATOR_PLACEHOLDER'
          )}
          triggerProps={{
            'aria-label': intl.get(
              'REQUEST_PAGE.BASIC_DETAILS.FIELDS.REQUEST_CREATOR'
            ),
            'data-testid': 'activeRequests-filters__requester-picker',
          }}
          popperProps={{
            'data-testid': 'requests-filters__requester__options-list',
          }}
          onFilterChange={onFilterChange}
          allowSelectDeactivated
          enableLazyLoading
          loadMoreOptions={loadMoreOptions}
        />
      </FormItem>
      <FormItem
        label={intl.get('REQUEST_PAGE.BASIC_DETAILS.FIELDS.REQUEST_OWNER')}
      >
        <UsersPicker
          deactivatedUserText={intl.get('DEACTIVATED')}
          usersList={ownerOptions}
          onChange={(users) => {
            updateFilters(REQUEST_TABLE_FILTERS.OWNERS, users);
          }}
          selectedUsersList={getInitialUsersPickerValue(
            ownerOptions,
            filters[REQUEST_TABLE_FILTERS.OWNERS] as string[]
          )}
          triggerText={intl.get(
            'REQUEST_PAGE.BASIC_DETAILS.FIELDS.REQUEST_OWNER_PLACEHOLDER'
          )}
          triggerProps={{
            'aria-label': intl.get(
              'REQUEST_PAGE.BASIC_DETAILS.FIELDS.REQUEST_OWNER'
            ),
            'data-testid': 'activeRequests-filters__owner-picker',
          }}
          popperProps={{
            'data-testid': 'requests-filters__owner__options-list',
          }}
          onFilterChange={onOwnerFilterChange}
          allowSelectDeactivated
          enableLazyLoading
          loadMoreOptions={loadMoreOwnerOptions}
        />
      </FormItem>
      <FormItem
        label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.LINKED_PROJECTS')}
      >
        <ProjectsDropdown
          onChange={(projects) =>
            updateFilters(REQUEST_TABLE_FILTERS.PROJECTS, projects)
          }
          values={filters[REQUEST_TABLE_FILTERS.PROJECTS] as string[]}
        />
      </FormItem>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.BUSINESS_UNIT')}>
        <MultiSelectDropdown
          options={businessUnitOptions}
          values={getInitialValueForDropDown(
            businessUnitOptions,
            filters[REQUEST_TABLE_FILTERS.BUSINESS_TEAMS] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(REQUEST_TABLE_FILTERS.BUSINESS_TEAMS, options)
          }
          triggerProps={{
            'aria-label': intl.get(
              'REQUESTS_LIST_PAGE.TABLE.HEAD.BUSINESS_UNIT'
            ),
            'data-testid': 'activeRequests-filters__business-teams-picker',
          }}
          listProps={{
            'data-testid':
              'activeRequests-filters__business-teams__options-list',
          }}
          onFilterChange={onBusinessUnitFilterChange}
          placeholder={intl.get('SELECT')}
          loadMoreOptions={loadMoreBusinessUnitOptions}
          enableLazyLoading
        />
      </FormItem>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.PRIORITY')}>
        <MultiSelectDropdown
          options={priorityOptions}
          values={getInitialValueForDropDown(
            priorityOptions,
            filters[REQUEST_TABLE_FILTERS.PRIORITY] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(REQUEST_TABLE_FILTERS.PRIORITY, options)
          }
          triggerProps={{
            'aria-label': intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.PRIORITY'),
            'data-testid': 'activeRequests-filters__priority-picker',
          }}
          listProps={{
            'data-testid': 'requests-filters__priority__options-list',
          }}
          placeholder={intl.get('SELECT')}
        />
      </FormItem>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.FORM')}>
        <MultiSelectDropdown
          options={formOptions}
          values={getInitialValueForDropDown(
            formOptions,
            filters[REQUEST_TABLE_FILTERS.FORM] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(REQUEST_TABLE_FILTERS.FORM, options)
          }
          triggerProps={{
            'aria-label': intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.FORM'),
            'data-cy': 'requests-filters__forms-picker',
          }}
          listProps={{
            'data-cy': 'requests-filters__forms__options-list',
          }}
          placeholder={intl.get('SELECT')}
          filterable
          enableLazyLoading
          loadMoreOptions={loadMoreForms}
          onFilterChange={onFormsFilterChange}
        />
      </FormItem>
      <FormItem
        label={intl.get(
          'REQUESTS_LIST_PAGE.TABLE.LINKED_PROJECTS.PICKER_LABEL'
        )}
      >
        <MultiSelectDropdown
          options={linkedProjectOptions}
          values={getInitialValueForDropDown(
            linkedProjectOptions,
            filters[REQUEST_TABLE_FILTERS.LINKED_PROJECTS] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(REQUEST_TABLE_FILTERS.LINKED_PROJECTS, options)
          }
          triggerProps={{
            'aria-label': intl.get(
              'REQUESTS_LIST_PAGE.TABLE.LINKED_PROJECTS.PICKER_LABEL'
            ),
          }}
          placeholder={intl.get('SELECT')}
        />
      </FormItem>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.CREATION_DATE')}>
        <div className='w-full flex space-x-4'>
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.FROM')}
            startDate={getInitialDatePickerValue(
              'from',
              filters[REQUEST_TABLE_FILTERS.CREATED_AT] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.CREATED_AT, date, 'from')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.CREATION_DATE'
              )}__from-range`,
              'data-testid': 'requests-filters__creation-date__from-range',
            }}
            data-testid='creation_date_filter'
          />
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.TO')}
            startDate={getInitialDatePickerValue(
              'to',
              filters[REQUEST_TABLE_FILTERS.CREATED_AT] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.CREATED_AT, date, 'to')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.CREATION_DATE'
              )}__to-range`,
              'data-cy': 'requests-filters__creation-date__to-range',
            }}
          />
        </div>
      </FormItem>
      <FormItem
        label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.SUBMISSION_DATE')}
      >
        <div className='w-full flex space-x-4'>
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.FROM')}
            startDate={getInitialDatePickerValue(
              'from',
              filters[REQUEST_TABLE_FILTERS.SUBMITTED_AT] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.SUBMITTED_AT, date, 'from')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.SUBMISSION_DATE'
              )}__from-range`,
              'data-testid': 'requests-filters__submission-date__from-range',
            }}
            data-testid='submission_date_filter'
          />
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.TO')}
            startDate={getInitialDatePickerValue(
              'to',
              filters[REQUEST_TABLE_FILTERS.SUBMITTED_AT] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.SUBMITTED_AT, date, 'to')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.SUBMISSION_DATE'
              )}__to-range`,
              'data-cy': 'requests-filters__submission-date__to-range',
            }}
          />
        </div>
      </FormItem>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.DECISION_DATE')}>
        <div className='w-full flex space-x-4'>
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.FROM')}
            startDate={getInitialDatePickerValue(
              'from',
              filters[REQUEST_TABLE_FILTERS.DECISION_DATE] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.DECISION_DATE, date, 'from')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.DECISION_DATE'
              )}__from-range`,
              'data-testid': 'requests-filters__decision-date__from-range',
            }}
            data-testid='decision_date_filter'
          />
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.TO')}
            startDate={getInitialDatePickerValue(
              'to',
              filters[REQUEST_TABLE_FILTERS.DECISION_DATE] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.DECISION_DATE, date, 'to')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.DECISION_DATE'
              )}__to-range`,
              'data-cy': 'requests-filters__decision-date__to-range',
            }}
          />
        </div>
      </FormItem>
      <FormItem label={intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.UPDATE_DATE')}>
        <div className='w-full flex space-x-4'>
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.FROM')}
            startDate={getInitialDatePickerValue(
              'from',
              filters[REQUEST_TABLE_FILTERS.UPDATED_AT] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.UPDATED_AT, date, 'from')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.UPDATE_DATE'
              )}__from-range`,
              'data-cy': 'requests-filters__update-date__from-range',
            }}
          />
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.TO')}
            startDate={getInitialDatePickerValue(
              'to',
              filters[REQUEST_TABLE_FILTERS.UPDATED_AT] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(REQUEST_TABLE_FILTERS.UPDATED_AT, date, 'to')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'REQUESTS_LIST_PAGE.TABLE.HEAD.UPDATE_DATE'
              )}__to-range`,
              'data-testid': 'requests-filters__update-date__to-range',
            }}
            data-testid='update_date_filter'
          />
        </div>
      </FormItem>
    </div>
  );
};

export default ActiveRequestsFiltersForm;
